import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, AppState, Easing, Platform, Pressable, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';

const BALLOON_COLORS = [
  ['charcoal', '#616161'], ['silver', '#B3B3B3'], ['gray', '#808080'],
];
const ROUND_SECONDS = 30;

const createStyles = colors => StyleSheet.create({
  game: { alignItems: 'center', gap: 8, maxWidth: '100%' },
  header: { width: '100%', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 6 },
  actions: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  smallButton: { minHeight: 32, minWidth: 44, paddingHorizontal: 8, alignItems: 'center', justifyContent: 'center', borderRadius: 8 },
  field: { borderRadius: 12, overflow: 'hidden', backgroundColor: colors.solid_color_primary },
  balloonPosition: { position: 'absolute', top: 0 },
  balloonTarget: { width: 56, height: 88, alignItems: 'center' },
  balloon: { width: 48, height: 58, borderRadius: 28, borderWidth: 1, borderColor: colors.text_color_secondary },
  shine: { position: 'absolute', left: 10, top: 10, width: 7, height: 17, borderRadius: 6, backgroundColor: '#fff', opacity: 0.4, transform: [{ rotate: '20deg' }] },
  knot: { width: 6, height: 6, marginTop: -1, transform: [{ rotate: '45deg' }] },
  string: { width: 1, height: 20, backgroundColor: colors.text_color_tertiary, opacity: 0.6 },
  overlay: { ...StyleSheet.absoluteFillObject, backgroundColor: colors.solid_color_primary, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 16 },
  playButton: { minHeight: 44, paddingHorizontal: 20, borderRadius: 10, backgroundColor: colors.text_color_primary, alignItems: 'center', justifyContent: 'center' },
});

const FloatingBalloon = React.memo(function FloatingBalloon({ balloon, height, active, styles, onPop, onExpire }) {
  const position = useRef(height - 60);
  const translateY = useRef(new Animated.Value(position.current)).current;
  useEffect(() => {
    if (!active) return undefined;
    const animation = Animated.timing(translateY, {
      toValue: -88,
      duration: Math.max(200, (position.current + 88) * balloon.speed),
      easing: Easing.linear,
      useNativeDriver: Platform.OS !== 'web',
    });
    animation.start(({ finished }) => { if (finished) onExpire(balloon.id); });
    return () => {
      animation.stop();
      translateY.stopAnimation(value => { position.current = value; });
    };
  }, [active, balloon.id, balloon.speed, onExpire, translateY]);

  return (
    <Animated.View style={[styles.balloonPosition, { left: balloon.x, transform: [{ translateY }] }]}>
      <Pressable
        testID={`waiting-balloon-${balloon.id}`}
        accessibilityRole="button"
        accessibilityLabel={`Pop ${balloon.name} balloon`}
        disabled={!active}
        onPress={() => onPop(balloon.id)}
        style={styles.balloonTarget}
      >
        <View style={[styles.balloon, { backgroundColor: balloon.color }]}><View style={styles.shine} /></View>
        <View style={[styles.knot, { backgroundColor: balloon.color }]} />
        <View style={styles.string} />
      </Pressable>
    </Animated.View>
  );
});

function WaitingBalloonGame({ colors, compact = false, fieldWidth, onHide }) {
  const styles = useMemo(() => createStyles(colors), [colors]);
  const width = fieldWidth ?? (compact ? 216 : 280);
  const height = compact ? 176 : 220;
  const [phase, setPhase] = useState('ready');
  const [balloons, setBalloons] = useState([]);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [remaining, setRemaining] = useState(ROUND_SECONDS);
  const balloonsRef = useRef([]);
  const scoreRef = useRef(0);
  const nextId = useRef(0);
  const pause = useCallback(() => setPhase(current => current === 'playing' ? 'paused' : current), []);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', state => { if (state !== 'active') pause(); });
    const onVisibility = () => { if (document.hidden) pause(); };
    if (Platform.OS === 'web') document.addEventListener('visibilitychange', onVisibility);
    return () => {
      subscription.remove();
      if (Platform.OS === 'web') document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pause]);

  const spawn = useCallback(() => {
    if (balloonsRef.current.length >= 6) return;
    const id = nextId.current++;
    const [name, color] = BALLOON_COLORS[id % BALLOON_COLORS.length];
    const balloon = { id, name, color, x: 8 + (id % 3) * (width - 72) / 2, speed: 17 + Math.random() * 6 };
    const next = [...balloonsRef.current, balloon];
    balloonsRef.current = next;
    setBalloons(next);
  }, [width]);

  useEffect(() => {
    if (phase !== 'playing') return undefined;
    const spawner = setInterval(spawn, 850);
    const clock = setInterval(() => setRemaining(current => Math.max(0, current - 1)), 1000);
    return () => { clearInterval(spawner); clearInterval(clock); };
  }, [phase, spawn]);
  useEffect(() => { if (remaining === 0 && phase === 'playing') setPhase('ended'); }, [remaining, phase]);

  const remove = useCallback(id => {
    const next = balloonsRef.current.filter(balloon => balloon.id !== id);
    balloonsRef.current = next;
    setBalloons(next);
  }, []);
  const pop = useCallback(id => {
    if (phase !== 'playing' || !balloonsRef.current.some(balloon => balloon.id === id)) return;
    remove(id);
    scoreRef.current += 1;
    setScore(scoreRef.current);
    setBest(current => Math.max(current, scoreRef.current));
  }, [phase, remove]);
  const play = () => {
    if (phase !== 'paused') {
      balloonsRef.current = [];
      setBalloons([]);
      scoreRef.current = 0;
      setScore(0);
      setRemaining(ROUND_SECONDS);
      spawn();
    }
    setPhase('playing');
  };

  return (
    <View style={styles.game} testID="waiting-balloon-game">
      <View style={styles.header}>
        <Text size="xs" weight="medium">Popped · {score} <Text size="xxs" color="secondary">Best {best}</Text></Text>
        <View style={styles.actions}>
          {phase === 'playing' && <Pressable accessibilityRole="button" accessibilityLabel="Pause Balloon Pop" onPress={pause} style={styles.smallButton} hitSlop={6}><Icon name="pause" size={12} color={colors.text_color_secondary} /></Pressable>}
          <Pressable accessibilityRole="button" accessibilityLabel="Hide Balloon Pop" onPress={onHide} style={styles.smallButton} hitSlop={6}><Text size="xs" color="secondary">Games</Text></Pressable>
        </View>
      </View>
      <View testID="waiting-balloon-field" style={[styles.field, { width, height }]}>
        {balloons.map(balloon => <FloatingBalloon key={balloon.id} balloon={balloon} height={height} active={phase === 'playing'} styles={styles} onPop={pop} onExpire={remove} />)}
        {phase !== 'playing' && (
          <View style={styles.overlay}>
            <Text size="m" weight="medium" accessibilityLiveRegion="polite">{phase === 'ready' ? 'Balloon Pop' : phase === 'paused' ? 'Paused' : `${score} ${score === 1 ? 'balloon' : 'balloons'} popped!`}</Text>
            {phase === 'ready' && <Text size="xs" color="secondary" style={{ textAlign: 'center' }}>Tap or click as many balloons as you can in 30 seconds.</Text>}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={phase === 'paused' ? 'Resume Balloon Pop' : phase === 'ended' ? 'Play Balloon Pop again' : 'Start Balloon Pop'}
              onPress={play}
              style={styles.playButton}
            >
              <Text size="s" weight="medium" style={{ color: colors.accent_text_color }}>{phase === 'paused' ? 'Resume' : phase === 'ended' ? 'Play again' : 'Let’s play'}</Text>
            </Pressable>
          </View>
        )}
      </View>
      <Text size="xxs" color="tertiary">{remaining}s · Pop the floating balloons</Text>
    </View>
  );
}

export default React.memo(WaitingBalloonGame);
